import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import UserService from '../utils/services/UserService';

const WatchContext = createContext();

export const useWatch = () => {
  const context = useContext(WatchContext);
  if (!context) {
    throw new Error('useWatch must be used within a WatchProvider');
  }
  return context;
};

export const WatchProvider = ({ children }) => {
  const { currentUserData } = useAuth();
  const [watchedUser, setWatchedUser] = useState(null);
  const [watchedUserData, setWatchedUserData] = useState(null);
  const [isWatching, setIsWatching] = useState(false);

  // Clear watched user when admin logs out
  useEffect(() => {
    // Only check admin status if we have valid user data
    if (currentUserData && !UserService.isUserAdmin(currentUserData)) {
      // Clear watched user state directly instead of calling stopWatching
      setWatchedUser(null);
      setIsWatching(false);
      sessionStorage.removeItem('watchedUser');
    }
  }, [currentUserData]);

  const startWatching = useCallback((user) => {
    if (!currentUserData || !UserService.isUserAdmin(currentUserData)) {
      console.warn('Only admin users can watch other users');
      return;
    }
    setWatchedUser(user);
    setIsWatching(true);
    sessionStorage.setItem('watchedUser', JSON.stringify(user));
  }, [currentUserData]);

  const stopWatching = useCallback(() => {
    // Always allow stopping watching - admin check is handled in startWatching
    setWatchedUser(null);
    setIsWatching(false);
    sessionStorage.removeItem('watchedUser');
  }, []);

  // Restore watched user from session storage on mount
  useEffect(() => {
    const storedWatchedUser = sessionStorage.getItem('watchedUser');
    if (storedWatchedUser && currentUserData && UserService.isUserAdmin(currentUserData)) {
      try {
        const user = JSON.parse(storedWatchedUser);
        setWatchedUser(user);
        setIsWatching(true);
      } catch (error) {
        console.error('Error restoring watched user:', error);
        sessionStorage.removeItem('watchedUser');
      }
    }
  }, [currentUserData]);

  const getEffectiveUser = () => {
    return isWatching ? watchedUser : currentUserData;
  };

  const getEffectiveUserId = () => {
    const effectiveId = isWatching ? watchedUser?.key : currentUserData?.key;
    return effectiveId;
  };

  // Helper function to safely check if user can perform admin actions
  const canPerformAdminAction = () => {
    return currentUserData && UserService.isUserAdmin(currentUserData);
  };

  const value = {
    watchedUser,
    watchedUserData,
    isWatching,
    startWatching,
    stopWatching,
    getEffectiveUser,
    getEffectiveUserId,
    canPerformAdminAction
  };

  return (
    <WatchContext.Provider value={value}>
      {children}
    </WatchContext.Provider>
  );
}; 